import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import videojs from 'video.js';
import 'videojs-markers/dist/videojs.markers.css';
import 'videojs-markers';
import 'videojs-sprite-thumbnails';
import 'video.js/dist/video-js.css';
import { useIntl } from 'react-intl';

import AnnotationWrapper from './File/View/AnnotationWrapper';

const VideoPlayer = ({
  file,
  fileVersion,
  comments,
  updateCommentsCount,
  sidebarTab,
  handleSidebarTab,
  readOnly,
}) => {
  const intl = useIntl();
  const videoRef = useRef(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const [player, setPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);
  const [userActivity, setUserActivity] = useState(false);

  const versionObject = fileVersion ? file.versions.find(v => v.id === fileVersion) : null;
  let src = versionObject ?
    versionObject.urls.preview_video || versionObject.urls.video :
    file.urls.preview_video || file.urls.video;

  // It's not possible to use VHS on iPhone, since there is not Media Source Extensions support.
  // This workaround allows to sign CF requests on Stockpress servers
  if (videojs.browser.IS_IPHONE) {
    src = src.replace(process.env.MIX_AWS_CF_DOMAIN, `${window.location.host}/hls`);
  }

  const videoType = versionObject ? versionObject.type : file.type;
  const isPreview = Boolean((versionObject && versionObject.urls.preview_video) ||
    (!versionObject && file.urls.preview_video));

  const getMarkerDuration = (time, markerDuration) => {
    if (videoDuration) {
      if (time + markerDuration > videoDuration) {
        return videoDuration - time;
      }

      return markerDuration;
    }
    return 0;
  };

  const markers = comments.filter(c => c.type === 'video').map(c => (
    {
      text: c.comment,
      time: c.meta.time,
      class: 'video-marker',
      duration: c.meta.duration ? getMarkerDuration(c.meta.time, c.meta.duration) : null,
    }
  ));

  const handlePlayPause = () => {
    if (player) {
      if (isPlaying) {
        player.pause();
      } else {
        player.play();
      }
    }
  };

  const emptyClick = () => {
    handlePlayPause();
  };

  const getAWSAuthenticationParamsFromUrl = (url) => {
    const AWSAuthenticationParamNames = [
      'Policy',
      'Signature',
      'Key-Pair-Id',
    ];
    const urlObject = new URL(url);
    const urlParams = new URLSearchParams(urlObject.search);
    const params = {};
    AWSAuthenticationParamNames.forEach((param) => {
      if (urlParams.get(param)) {
        params[param] = urlParams.get(param);
      }
    });
    return new URLSearchParams(params).toString();
  };

  useEffect(() => {
    if (videoRef.current && playerReady) {
      const playerObject = videoRef.current;
      if (playerObject.markers) {
        playerObject.markers.removeAll();
        playerObject.markers.add(markers);
      }
    }
  }, [markers, playerReady]);

  useEffect(() => {
    if (videoRef.current) {
      const playerInstance = videojs(videoRef.current, {
        controls: true,
        autoplay: false,
        fluid: true,
        preload: 'auto',
        aspectRatio: '16:9',
        playbackRates: [0.5, 1, 1.5, 2],
        inactivityTimeout: 0,
        notSupportedMessage: intl.formatMessage({
          defaultMessage: 'Your video is being processed, please check back later.',
        }),
        plugins: {
          spriteThumbnails: {
            width: 280,
            height: 158,
            columns: 14,
            rows: 25,
            interval: 3,
            downlink: 0,
          },
        },
        html5: {
          vhs: {
            limitRenditionByPlayerDimensions: true,
            withCredentials: false,
            maxPlaylistRetries: 2,
            useDevicePixelRatio: true,
            useNetworkInformationApi: true,
            overrideNative: true,
          },
          nativeAudioTracks: videojs.browser.IS_SAFARI,
          nativeVideoTracks: videojs.browser.IS_SAFARI,
          nativeTextTracks: false,
        },
      });

      if (process.env.APP_ENV === 'production') {
        videojs.log.level('error');
      }

      playerInstance.on('loadedmetadata', () => {
        const duration = playerInstance.duration();
        setVideoDuration(duration);
      });

      playerInstance.on('timeupdate', () => {
        setCurrentTime(playerInstance.currentTime());
      });

      playerInstance.on('ready', () => {
        setPlayerReady(true);
        playerInstance.markers({
          markers: [],
          markerStyle: {
            'background-color': '',
          },
          markerTip: {
            display: false,
            text(marker) {
              return marker.text;
            },
            time(marker) {
              return marker.time;
            },
          },
        });
      });

      setPlayer(playerInstance);
      videoRef.current = playerInstance;

      return () => {
        if (playerInstance) {
          playerInstance.dispose();
          setPlayerReady(false);
        }
      };
    }
    return () => {};
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (player && playerReady) {
      if (isPlaying) {
        player.pause();
      }

      let type = isPreview ? 'video/mp4' : videoType;
      let spriteThumbnailsUrlArray = [];
      if (src && src.includes('m3u8')) {
        type = 'application/x-mpegURL';
        const urlParams = getAWSAuthenticationParamsFromUrl(src);

        player.on('xhr-hooks-ready', () => {
          const playerXhrRequestHook = (options) => {
            // eslint-disable-next-line no-param-reassign
            options.beforeSend = (xhr) => {
              // eslint-disable-next-line no-param-reassign
              xhr.url = `${xhr.url.split('?')[0]}?${urlParams}`;
              // eslint-disable-next-line no-param-reassign
              xhr.uri = `${xhr.url.split('?')[0]}?${urlParams}`;
              xhr.open(xhr.method, xhr.uri, xhr.async);
            };
            return options;
          };
          if (urlParams) {
            player.tech().vhs.xhr.onRequest(playerXhrRequestHook);
          }
        });

        if (file.attributes.thumbnail_sprites) {
          spriteThumbnailsUrlArray = file.attributes.thumbnail_sprites.map(tsurl => (`https://${process.env.MIX_AWS_CF_DOMAIN}/${tsurl}?${urlParams}`));
        }
      }

      player.spriteThumbnails().options.urlArray = spriteThumbnailsUrlArray;
      player.spriteThumbnails().setState({ ready: spriteThumbnailsUrlArray.length > 0 });
      player.reset();
      player.src({ src, type });
      player.poster(file.urls.preview);
      player.playbackRates([0.5, 1, 1.5, 2]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.urls.preview, isPreview, player, playerReady, src, videoType]);

  useEffect(() => {
    setPlayerReady(false);
    setIsPlaying(false);
  }, [src]);

  useEffect(() => {
    let userActivityTimeout = null;
    if (player) {
      player.on('play', () => {
        setIsPlaying(true);
      });

      player.on('pause', () => {
        setIsPlaying(false);
      });

      player.on('mousemove', () => {
        clearTimeout(userActivityTimeout);
        setUserActivity(true);

        userActivityTimeout = setTimeout(() => {
          setUserActivity(false);
        }, 1000);
      });
    }
  }, [player]);

  return (
    <div className={classnames({ 'video-js-active': userActivity, 'video-js-inactive': !userActivity })}>
      <div data-vjs-player>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video ref={videoRef} controls className="video-js" />
      </div>
      <AnnotationWrapper
        type="video"
        file={file}
        fileVersion={fileVersion}
        handleParentAnnotationTip={() => {}}
        emptyClick={emptyClick}
        currentTime={currentTime}
        videoRef={videoRef}
        updateCommentsCount={updateCommentsCount}
        sidebarTab={sidebarTab}
        handleSidebarTab={handleSidebarTab}
        readOnly={readOnly}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  comments: state.Files.comments,
});

VideoPlayer.propTypes = {
  file: PropTypes.object.isRequired,
  fileVersion: PropTypes.number,
  comments: PropTypes.array.isRequired,
  updateCommentsCount: PropTypes.func.isRequired,
  sidebarTab: PropTypes.string.isRequired,
  handleSidebarTab: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

VideoPlayer.defaultProps = {
  fileVersion: null,
  readOnly: false,
};

export default connect(mapStateToProps)(VideoPlayer);
